import { useData } from "../../../runtime/provider";
import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { YouBreakdownModalSWMSheet } from "@components/modals/YouBreakdownModalSWM";
import { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import {
  ActivityIndicator,
  Image,
  type ImageSourcePropType,
  LayoutChangeEvent,
} from "react-native";
import Animated, {
  FadeIn,
  FadeInUp,
  FadeOut,
  FadeOutUp,
  LinearTransition,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { trpc } from "@utils/trpc";
import { getQueryKey } from "@trpc/react-query";
import { useQuery } from "@tanstack/react-query";

import {
  artistSongBreakdownWireSchema,
  type ArtistSongBreakdown,
} from "@libs/wire";
import { formatMoney } from "@utils/formatting/money";
import { modal } from "@utils/modal/useModal";
import { getActiveTab } from "@hooks/useActiveTab";
import { router } from "expo-router";
import { create } from "zustand";
import {
  levelImages,
  levelBadgeConfig,
  DEFAULT_BADGE_CONFIG,
} from "./LevelBadge";
import LGradient from "@components/LGradient";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";
import {
  resolveTopArtistsTimeframe,
  type Timeframe,
} from "./topArtistsTimeframe";

export type { Timeframe } from "./topArtistsTimeframe";

export const ARTIST_ROW_HEIGHT = 70;

// Figma design colors
const STREAM_COUNT_COLOR = "#3D83F7";
const ACCESS_COUNT_COLOR = "#F67317";
const MONEY_COLOR = "#10B981"; // Green for money
const SECTION_HEADER_COLOR = "rgba(255, 255, 255, 0.5)";
const SHOW_ALL_COLOR = "#B9BABA";

// Fan level badge — level icon + gradient text on gradient bg
const FanLevelBadge = ({ levelName }: { levelName: string }) => {
  const key = levelName.toLowerCase();
  const config = levelBadgeConfig[key] ?? DEFAULT_BADGE_CONFIG;
  const icon = levelImages[key];
  return (
    <LGradient
      colors={[config.bgTop, "#101010"]}
      gradientDirection="vertical"
      flexDirection="row"
      alignItems="center"
      gap="s1"
      paddingHorizontal="s1_5"
      paddingVertical="s1"
      borderRadius="max"
      style={{
        borderWidth: 0.5,
        borderColor: config.border,
      }}
    >
      {icon && <Image source={icon} style={{ width: 14, height: 14 }} />}
      <LText
        variant="Caption/C2/SemiBold"
        gradientColours={[config.textLeft, config.textRight]}
        gradientDirection="horizontal"
      >
        {levelName}
      </LText>
    </LGradient>
  );
};

export interface TopArtist {
  artistId: string;
  artistName: string;
  artistImage?: string | null;
  streamCount: number;
  streamTime: number;
  accessesCount: number;
  moneySpentStreaming?: number;
  moneySpentD2C?: number;
  // Per-artist fan score fields
  score: number;
  level: number;
  levelName: string;
  nextLevelName: string | null;
  levelProgress: number;
  shadowColor: string;
}

// ── Shared Zustand store ──────────────────────────────────────
type TimeframeStore = {
  timeframe: Exclude<Timeframe, "allTime">;
  setTimeframe: (t: Exclude<Timeframe, "allTime">) => void;
};

export const useTimeframeStore = create<TimeframeStore>((set) => ({
  timeframe: "thisMonth",
  setTimeframe: (timeframe) => set({ timeframe }),
}));

// ── Props ─────────────────────────────────────────────────────
export interface TopArtistsListProps {
  artists: TopArtist[];
  thisMonthArtists?: TopArtist[];
  maxVisible?: number;
  onCloseModal?: () => void;
  onShowAll?: () => void;
  showHeader?: boolean;
  currencySymbol?: string;
  userId?: string;
  isInModal?: boolean;
  showBreakdown?: boolean;
}

export interface ArtistRowProps {
  artist: TopArtist;
  rank: number;
  currencySymbol?: string;
  timeframe: Timeframe;
  isInModal?: boolean;
  showBreakdown?: boolean;
  onCloseModal?: () => void;
}

// ── Helpers ───────────────────────────────────────────────────
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return String(num);
};

// ── Badge components ──────────────────────────────────────────
const StatBadge = ({
  iconSource,
  value,
  color,
}: {
  iconSource: ImageSourcePropType;
  value: number;
  color: string;
}) => (
  <LBox
    flexDirection="row"
    alignItems="center"
    gap="s1"
    paddingHorizontal="s2"
    borderRadius="max"
    height={22}
    style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
  >
    <Image source={iconSource} style={{ width: 16, height: 16 }} />
    <LText variant="Caption/C1/SemiBold" style={{ color }}>
      {formatNumber(value)}
    </LText>
  </LBox>
);

const MoneyBadge = ({ value, color }: { value: string; color: string }) => (
  <LBox
    flexDirection="row"
    alignItems="center"
    gap="s1"
    paddingHorizontal="s2"
    borderRadius="max"
    height={22}
    style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
  >
    <LText variant="Caption/C1/SemiBold" style={{ color }}>
      {value}
    </LText>
  </LBox>
);

// ── Song / Experiences rows ───────────────────────────────────
interface SongRowProps {
  songTitle: string;
  streamCount: number;
  moneySpentStreaming: number;
  currencySymbol?: string;
}

const SongRow = ({
  songTitle,
  streamCount,
  moneySpentStreaming,
  currencySymbol = "£",
}: SongRowProps) => (
  <LBox
    flexDirection="row"
    alignItems="center"
    paddingVertical="s1"
    paddingLeft="s3"
    gap="s2"
  >
    <LBox flex={1}>
      <LText
        variant="Caption/C2/Regular"
        numberOfLines={1}
        style={{ color: "rgba(255, 255, 255, 0.7)" }}
      >
        {songTitle}
      </LText>
    </LBox>
    <LBox flexDirection="row" alignItems="center" gap="s2">
      <StatBadge
        iconSource={require("@assets/stats/streams_icon.png")}
        value={streamCount}
        color={STREAM_COUNT_COLOR}
      />
      <MoneyBadge
        value={formatMoney(moneySpentStreaming, currencySymbol)}
        color={MONEY_COLOR}
      />
    </LBox>
  </LBox>
);

// ── ArtistRow ─────────────────────────────────────────────────
export const ArtistRow = memo(
  ({
    artist,
    rank,
    currencySymbol = "£",
    timeframe,
    isInModal,
    showBreakdown = true,
    onCloseModal,
  }: ArtistRowProps) => {
    const session = useData();
    const [isExpanded, setIsExpanded] = useState(false);
    const bottomSheetManager = useBottomSheetManager();

    const queryInput = { artistId: artist.artistId, timeframe } as const;

    const breakdownQueryKey = getQueryKey(
      trpc.user.stats.getArtistSongBreakdown,
      queryInput,
      "query",
    );

    const { data, isLoading } = useQuery({
      ...session.cache.durableQueryOptions<ArtistSongBreakdown>({
        queryKey: breakdownQueryKey,
        queryFn: () =>
          session.api.user.stats.getArtistSongBreakdown.query(queryInput),
        schema: artistSongBreakdownWireSchema,
      }),
      enabled: isExpanded,
    });

    // When the artist's stream count updates in real-time,
    // invalidate the cached song breakdown so it refetches from Postgres
    const prevStreamCount = useRef(artist.streamCount);
    useEffect(() => {
      if (prevStreamCount.current !== artist.streamCount) {
        prevStreamCount.current = artist.streamCount;
        if (isExpanded) {
          session.queryClient
            .invalidateQueries({ queryKey: breakdownQueryKey })
            .catch(console.error);
        }
      }
    }, [
      artist.streamCount,
      artist.artistId,
      isExpanded,
      timeframe,
      breakdownQueryKey,
    ]);

    const handleToggle = useCallback(() => {
      if (!showBreakdown) {
        return;
      }
      setIsExpanded((prev) => !prev);
    }, [showBreakdown]);

    // Image tap → navigate to artist community
    const handleImagePress = useCallback(() => {
      if (isInModal) {
        onCloseModal?.();
        if (!onCloseModal) {
          modal.closeAllModals();
        }
      }
      router.push({
        pathname: `/(app)/(tabs)/${getActiveTab()}/artist/[artistId]/community`,
        params: { artistId: artist.artistId },
      });
    }, [artist.artistId, isInModal, onCloseModal]);

    // Name/middle tap → open breakdown modal
    const handleNamePress = useCallback(() => {
      if (!showBreakdown) {
        return;
      }
      bottomSheetManager.open(
        <YouBreakdownModalSWMSheet
          artistName={artist.artistName}
          score={artist.score}
          levelName={artist.levelName}
          nextLevelName={artist.nextLevelName}
          levelProgress={artist.levelProgress}
          shadowColor={artist.shadowColor}
          streamTime={artist.streamTime}
          accessesCount={artist.accessesCount}
          moneySpentStreaming={artist.moneySpentStreaming ?? 0}
          moneySpentD2C={artist.moneySpentD2C ?? 0}
        />,
        {
          mode: "push",
        },
      );
    }, [artist, bottomSheetManager, showBreakdown]);

    return (
      <LBox layout={LinearTransition}>
        <LBox
          alignItems="center"
          flexDirection="row"
          height={ARTIST_ROW_HEIGHT}
          gap="s4"
        >
          {/* Left zone: rank + image → navigate to community */}
          <LBox flexDirection="row" alignItems="center" gap="s4">
            <LBox width={15} alignItems="center" justifyContent="center">
              <LText
                variant="Caption/C2/Medium"
                style={{ color: "rgba(255, 255, 255, 0.7)" }}
              >
                {rank}
              </LText>
            </LBox>

            <LPressable
              onPress={handleImagePress}
              sentryEventName="top-artists-artist-navigate"
            >
              <LArtwork
                borderRadius="max"
                size={44}
                imageSize="small"
                url={artist.artistImage}
                userAvatar={false}
              />
            </LPressable>
          </LBox>

          {/* Middle zone: name + fan level → open breakdown modal */}
          <LPressable
            onPress={handleNamePress}
            sentryEventName="top-artists-breakdown"
            style={{ flex: 1, justifyContent: "center" }}
          >
            <LBox gap="s1">
              <LText variant="Body/B2/SemiBold" numberOfLines={1}>
                {artist.artistName}
              </LText>
              <LBox flexDirection="row" alignItems="center">
                <FanLevelBadge levelName={artist.levelName} />
              </LBox>
            </LBox>
          </LPressable>

          {/* Right zone: chevron → expand/collapse */}
          {showBreakdown ? (
            <LPressable
              onPress={handleToggle}
              sentryEventName="top-artists-list-item"
            >
              <LBox
                width={26}
                height={26}
                alignItems="center"
                justifyContent="center"
                borderRadius="max"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.05)" }}
              >
                <LText
                  variant="Caption/C2/Medium"
                  style={{ color: "rgba(255, 255, 255, 0.5)" }}
                >
                  {isExpanded ? "▲" : "▼"}
                </LText>
              </LBox>
            </LPressable>
          ) : (
            <LBox width={26} />
          )}
        </LBox>

        {isExpanded && (
          <Animated.View
            layout={LinearTransition.duration(200)}
            entering={FadeInUp.duration(150)}
            exiting={FadeOutUp.duration(150)}
            style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              marginLeft: 16,
              borderColor: "rgba(255, 255, 255, 0.1)",
              backgroundColor: "rgba(255, 255, 255, 0.02)",
            }}
          >
            {/* Per-artist stats: streams, experiences, money */}
            <LBox
              flexDirection="row"
              alignItems="center"
              gap="s2"
              paddingBottom="s2"
            >
              <StatBadge
                iconSource={require("@assets/stats/streams_icon.png")}
                value={artist.streamCount}
                color={STREAM_COUNT_COLOR}
              />
              <StatBadge
                iconSource={require("@assets/stats/experience_icon.png")}
                value={artist.accessesCount}
                color={ACCESS_COUNT_COLOR}
              />
              <MoneyBadge
                value={formatMoney(
                  (artist.moneySpentStreaming ?? 0) +
                    (artist.moneySpentD2C ?? 0),
                  currencySymbol,
                )}
                color={MONEY_COLOR}
              />
            </LBox>

            {/* Song breakdown */}
            <LBox paddingTop="s2">
              <LBox paddingBottom="s1">
                <LText
                  variant="Caption/C1/Medium"
                  style={{ color: "rgba(255, 255, 255, 0.6)" }}
                >
                  Songs
                </LText>
              </LBox>

              {isLoading ? (
                <Animated.View
                  key="loading"
                  entering={FadeIn.duration(150)}
                  exiting={FadeOut.duration(150)}
                  style={{ paddingVertical: 4, alignItems: "center" }}
                >
                  <ActivityIndicator size="small" color={STREAM_COUNT_COLOR} />
                </Animated.View>
              ) : data?.songs && data.songs.length > 0 ? (
                <Animated.View key="songs" entering={FadeIn.duration(150)}>
                  {data.songs.map((song) => (
                    <SongRow
                      key={song.songId}
                      songTitle={song.songTitle}
                      streamCount={song.streamCount}
                      moneySpentStreaming={song.moneySpentStreaming}
                      currencySymbol={currencySymbol}
                    />
                  ))}
                </Animated.View>
              ) : (
                <Animated.View key="empty" entering={FadeIn.duration(150)}>
                  <LBox paddingVertical="s2" paddingLeft="s4">
                    <LText variant="Caption/C2/Regular" color="subText">
                      No songs found
                    </LText>
                  </LBox>
                </Animated.View>
              )}
            </LBox>
          </Animated.View>
        )}
      </LBox>
    );
  },
);

ArtistRow.displayName = "ArtistRow";

// ── Animated Timeframe Toggle ─────────────────────────────────
const TOGGLE_OPTIONS: {
  key: Exclude<Timeframe, "allTime">;
  label: string;
}[] = [
  { key: "12months", label: "12-Month" },
  { key: "thisMonth", label: "This Month" },
];

export const TimeframeToggle = () => {
  const { timeframe, setTimeframe } = useTimeframeStore();

  const widths = useRef<number[]>([0, 0]);
  const offsets = useRef<number[]>([0, 0]);
  const indicatorX = useSharedValue(0);
  const indicatorW = useSharedValue(0);

  const activeIndex = timeframe === "12months" ? 0 : 1;

  // Animate indicator when activeIndex or measurements change
  const updateIndicator = useCallback(
    (idx: number) => {
      indicatorX.set(withTiming(offsets.current[idx], { duration: 200 }));
      indicatorW.set(withTiming(widths.current[idx], { duration: 200 }));
    },
    [indicatorX, indicatorW],
  );

  useEffect(() => {
    if (widths.current[activeIndex] > 0) {
      updateIndicator(activeIndex);
    }
  }, [activeIndex, updateIndicator]);

  const handleLayout = useCallback(
    (index: number) => (e: LayoutChangeEvent) => {
      const { width, x } = e.nativeEvent.layout;
      widths.current[index] = width;
      offsets.current[index] = x;
      // Set initial position without animation
      if (index === activeIndex) {
        indicatorX.value = x;
        indicatorW.value = width;
      }
    },
    [activeIndex, indicatorX, indicatorW],
  );

  const indicatorStyle = useAnimatedStyle(() => ({
    position: "absolute" as const,
    left: indicatorX.value,
    width: indicatorW.value,
    top: 0,
    bottom: 0,
    borderRadius: 999,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
  }));

  return (
    <LBox
      flexDirection="row"
      borderRadius="max"
      style={{ backgroundColor: "rgba(255, 255, 255, 0.08)" }}
    >
      <Animated.View style={indicatorStyle} />
      {TOGGLE_OPTIONS.map((opt, i) => (
        <LPressable
          key={opt.key}
          onPress={() => setTimeframe(opt.key)}
          onLayout={handleLayout(i)}
          sentryEventName={`timeframe-toggle-${opt.key}`}
        >
          <LBox paddingHorizontal="s3" paddingVertical="s1_5">
            <LText
              variant="Caption/C2/SemiBold"
              style={{
                color:
                  timeframe === opt.key ? "#fff" : "rgba(255, 255, 255, 0.5)",
              }}
            >
              {opt.label}
            </LText>
          </LBox>
        </LPressable>
      ))}
    </LBox>
  );
};

// ── TopArtistsList ────────────────────────────────────────────
export const TopArtistsList = ({
  artists,
  thisMonthArtists,
  maxVisible = 3,
  onShowAll,
  showHeader = true,
  currencySymbol = "£",
  userId,
  isInModal,
  showBreakdown = true,
  onCloseModal,
}: TopArtistsListProps) => {
  const selectedTimeframe = useTimeframeStore((s) => s.timeframe);
  const timeframe = resolveTopArtistsTimeframe(
    selectedTimeframe,
    Boolean(thisMonthArtists),
  );

  const activeArtists =
    timeframe === "thisMonth" && thisMonthArtists ? thisMonthArtists : artists;
  const visibleArtists = useMemo(
    () => activeArtists.slice(0, maxVisible),
    [activeArtists, maxVisible],
  );
  const hasMore = activeArtists.length > maxVisible;
  const canShowBreakdown = showBreakdown && !userId;

  if (artists.length === 0) {
    return null;
  }

  return (
    <LBox gap="s3">
      {/* Section Header + Toggle */}
      {(showHeader || thisMonthArtists) && (
        <LBox
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {showHeader && (
            <LText
              variant="Caption/C1/Medium"
              style={{ color: SECTION_HEADER_COLOR }}
            >
              {userId ? "TOP ARTISTS" : "YOUR TOP ARTISTS"}
            </LText>
          )}
          {thisMonthArtists && <TimeframeToggle />}
        </LBox>
      )}

      {/* Artist List */}
      {visibleArtists.length > 0 ? (
        <LBox gap="s2" layout={LinearTransition}>
          {visibleArtists.map((artist, index) => (
            <ArtistRow
              key={`${artist.artistId}-${timeframe}`}
              artist={artist}
              rank={index + 1}
              currencySymbol={currencySymbol}
              timeframe={timeframe}
              isInModal={isInModal}
              showBreakdown={canShowBreakdown}
              onCloseModal={onCloseModal}
            />
          ))}
        </LBox>
      ) : (
        <LBox paddingVertical="s4" alignItems="center">
          <LText variant="Body/B2/Regular" color="subText">
            No artists this month yet
          </LText>
        </LBox>
      )}

      {/* Show All Link */}
      {hasMore && onShowAll && (
        <LPressable
          onPress={onShowAll ?? (() => {})}
          sentryEventName="top-artists-show-all"
        >
          <LBox paddingVertical="s2" alignItems="center">
            <LText
              variant="Caption/C1/Medium"
              style={{ color: SHOW_ALL_COLOR }}
            >
              Show all
            </LText>
          </LBox>
        </LPressable>
      )}
    </LBox>
  );
};

export default TopArtistsList;
